import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { APP } from '../config/app';
import AccessibilityPanel from '../components/common/AccessibilityPanel';

/**
 * Per-user reading-adaptation preferences ("Read & Adapt").
 *
 * A single place to opt into the adjustments neurodivergent / low-vision /
 * visual-stress profiles asked for: font family, larger text, extra reading
 * spacing, a whole-page colour overlay (Irlen-style), a calmer layout, and a
 * motion override. Preferences persist in localStorage (per browser) and are
 * applied as data-* attributes + a font-size on <html>, which accessibility.css
 * and re-pointed font tokens respond to. Because tokens are rem-based, scaling
 * the root font-size cleanly enlarges everything.
 */

const STORAGE_KEY = APP.storageKey('a11y_prefs');

const DEFAULTS = {
  fontType: 'default', // 'default' | 'sans' | 'serif' | 'dyslexic'
  fontScale: 1, // 0.9 – 1.5 (rem scale on <html>)
  readable: 0, // reading spacing level: 0 | 1 | 2
  overlay: 'none', // colour tint: none|cream|yellow|rose|blue|green|grey
  overlayIntensity: 0.12, // 0.05 – 0.30 opacity
  calm: false, // hide discovery/AI panels, reduce visual noise
  reduceMotion: false, // force-disable animations regardless of OS setting
};

/** rgb triples for the whole-page colour overlay. */
const OVERLAY_COLORS = {
  none: null,
  cream: '255, 248, 220',
  yellow: '255, 236, 150',
  rose: '255, 204, 213',
  blue: '173, 216, 230',
  green: '200, 230, 201',
  grey: '176, 176, 176',
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    // Migrate legacy shape: readable used to be a boolean.
    if (typeof parsed.readable === 'boolean') parsed.readable = parsed.readable ? 1 : 0;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

const AccessibilityContext = createContext(null);

export function AccessibilityProvider({ children }) {
  const [prefs, setPrefs] = useState(loadPrefs);

  // Apply preferences to the document root and persist them.
  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = prefs.fontScale === 1 ? '' : `${Math.round(prefs.fontScale * 100)}%`;
    root.setAttribute('data-a11y-font', prefs.fontType);
    root.setAttribute('data-a11y-readable', String(prefs.readable || 0));
    root.setAttribute('data-a11y-calm', prefs.calm ? 'true' : 'false');
    root.setAttribute('data-a11y-reduce-motion', prefs.reduceMotion ? 'true' : 'false');
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* storage full / disabled — preferences just won't persist */
    }
  }, [prefs]);

  const setPref = useCallback((key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => setPrefs({ ...DEFAULTS }), []);

  const overlayStyle = useMemo(() => {
    const rgb = OVERLAY_COLORS[prefs.overlay];
    if (!rgb) return null;
    return { backgroundColor: `rgba(${rgb}, ${prefs.overlayIntensity})` };
  }, [prefs.overlay, prefs.overlayIntensity]);

  return (
    <AccessibilityContext.Provider value={{ prefs, setPref, reset }}>
      {children}
      {overlayStyle && <div className="a11y-overlay" aria-hidden="true" style={overlayStyle} />}
      <AccessibilityPanel />
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error('useAccessibility must be used within an AccessibilityProvider');
  return ctx;
}
